/**
 * Keycloak configuration for the on-prem build (MOD-885 / on-prem auth
 * workstream). Reads `NEXT_PUBLIC_KEYCLOAK_*` env vars and fail-fast
 * validates them at module load when `isKeycloak === true`.
 *
 * Cloud builds skip validation entirely so a cloud-deploy pipeline doesn't
 * fail because a hospital-specific Keycloak env var isn't set. The exports
 * are still typed `string`; on cloud they default to empty strings when
 * unset (and pass through any value that is explicitly provided, e.g. an
 * engineer running a local Keycloak). Either way, the Keycloak hooks are
 * gated behind `isKeycloak` so the cloud values are never read at runtime.
 *
 * ## Library decision (records why these are the only two env vars)
 *
 * For the on-prem FE auth flow we use **two libraries**, mirroring the
 * existing Kinde split between web and Tauri:
 *
 * - **Web build (Next.js):** `next-auth@4` (the last Pages-Router-native
 *   NextAuth major). Built-in Keycloak provider, refresh-token handling
 *   via the `jwt` callback, middleware helpers, session storage. Mounts
 *   at the single Pages-Router catch-all `src/pages/api/auth/[...nextauth].ts`,
 *   which dispatches on `isKeycloak` to either `handleAuth()` (Kinde,
 *   cloud) or `NextAuth(authOptions)` (Keycloak, on-prem). Only one
 *   catch-all is permitted per directory; consolidating the two
 *   providers under it lets cloud Kinde and on-prem NextAuth coexist
 *   without route ambiguity.
 *
 *   v5 / Auth.js was evaluated and rejected for this tranche: it targets
 *   the App Router as the default and the Pages-Router compat path
 *   requires a separate migration. We stay on v4 until the rest of
 *   scribe-fe-v2 migrates off Pages Router.
 * - **Tauri build (desktop):** `oidc-client-ts`. NextAuth requires Next.js
 *   API routes for the OAuth callback; Tauri has no Next server, so we use
 *   a pure browser-side OIDC client. Same deal as the existing
 *   `@kinde-oss/kinde-auth-react` for desktop today.
 * - **Unified `useAuth()` hook (T5):** branches on `IS_TAURI` to dispatch
 *   to the right library, returning the same shape both ways. Same pattern
 *   as the current `useKinde()` hook.
 *
 * Both libraries take only the **issuer URL** and **client ID** as
 * config — they derive every other endpoint (auth, token, JWKS, end-session)
 * from the OIDC discovery document at `${issuer}/.well-known/openid-configuration`.
 * No client secret because the realm's `scribe-fe` client is a public
 * PKCE-only client (per [MOD-884](https://linear.app/getheidi/issue/MOD-884)).
 *
 * Redirect URIs are derived at runtime from `window.location.origin` so
 * one build can run against multiple deployment hostnames without rebuilding;
 * the realm's `redirectUris` allowlist (templated via
 * `${SCRIBE_FE_REDIRECT_URI_PATTERN}` at install time) gates which origins
 * Keycloak will accept.
 */

import { getKeycloakIssuerUrl as getRuntimeKeycloakIssuerUrl } from '@/tauri/onprem/serverConfig';
import { isTauri } from '@/tauri/utils';
import { isKeycloak } from './buildProfile';

const rawIssuer = process.env.NEXT_PUBLIC_KEYCLOAK_ISSUER_URL;
const rawClientId = process.env.NEXT_PUBLIC_KEYCLOAK_CLIENT_ID;

function requireKeycloakEnv(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `[keycloakConfig] On-prem build (NEXT_PUBLIC_AUTH_PROVIDER=keycloak) requires ${name} to be set. ` +
        'See bootstrap/keycloak/README.md in the ml-scribe repo.'
    );
  }
  return value;
}

function requireValidIssuerUrl(value: string): string {
  // On-prem placeholder bypass: at Docker build time, env vars may be set
  // to __ONPREM_*__ placeholder tokens that aren't valid URLs.
  // onprem-runtime-config.js replaces them at container startup with the
  // real deployment URL, so this validator runs against the placeholder
  // only during static export, never at runtime.
  // Tauri builds also use this placeholder — the runtime issuer URL is
  // resolved from localStorage via `getRuntimeKeycloakIssuerUrl()`.
  if (value.startsWith('__ONPREM_') && value.endsWith('__')) {
    return value;
  }
  // Parse-check at module load so a malformed issuer (e.g. missing protocol,
  // `keycloak.hospital.local/realms/heidi`) surfaces at the same fail-fast
  // point as the presence check — not later as an opaque network error
  // during OIDC discovery. Both `next-auth` and `oidc-client-ts` build every
  // downstream URL from this value, so anything `new URL()` rejects here is
  // guaranteed to fail at runtime.
  try {
    new URL(value);
  } catch {
    throw new Error(
      `[keycloakConfig] NEXT_PUBLIC_KEYCLOAK_ISSUER_URL is not a valid URL: ${JSON.stringify(value)}. ` +
        'Include the protocol (e.g. https://keycloak.hospital.local/realms/heidi).'
    );
  }
  return value;
}

// ON-PREM-GATE: F1 module-load gate. Keycloak issuer URL is only required
// (and only present in the runtime bundle's required-env list) when the
// build runs against an on-prem auth provider. Cloud builds export an
// empty string when the env var is unset (or pass the value through if
// it's explicitly provided); `isKeycloak`-gated consumers never read it.
//
// Tauri on-prem builds: the build-time value is a __ONPREM_*__ placeholder
// (or empty). The runtime issuer URL is resolved from localStorage via
// `getRuntimeKeycloakIssuerUrl()` (see src/tauri/onprem/serverConfig.ts).
// The exported `KEYCLOAK_ISSUER_URL` constant keeps the build-time value
// for module-load compatibility; Tauri consumers use
// `getRuntimeKeycloakIssuerUrl()` instead.
export const KEYCLOAK_ISSUER_URL: string = isKeycloak
  ? requireValidIssuerUrl(
      requireKeycloakEnv('NEXT_PUBLIC_KEYCLOAK_ISSUER_URL', rawIssuer)
    )
  : (rawIssuer ?? '');

// ON-PREM-GATE: F1 module-load gate. Keycloak client ID is only required
// when AUTH_PROVIDER=keycloak. Cloud builds export an empty string when
// unset (or pass through the configured value); `isKeycloak`-gated
// consumers never read it.
export const KEYCLOAK_CLIENT_ID: string = isKeycloak
  ? requireKeycloakEnv('NEXT_PUBLIC_KEYCLOAK_CLIENT_ID', rawClientId)
  : (rawClientId ?? '');

/**
 * Runtime Keycloak issuer URL for Tauri/desktop on-prem builds.
 *
 * In the web build, `KEYCLOAK_ISSUER_URL` (build-time constant) is correct
 * because onprem-runtime-config.js replaces the placeholder at container
 * startup. In the Tauri build, there is no runtime replacement step — the
 * issuer URL is resolved from the user-configured server URL in localStorage.
 *
 * Returns the build-time constant for web builds, or the runtime-resolved
 * URL for Tauri builds. Returns `null` if the server URL is not yet
 * configured (callers should redirect to the server-setup page).
 */
export function getRuntimeKeycloakIssuer(): string {
  if (isTauri()) {
    const runtimeUrl = getRuntimeKeycloakIssuerUrl();
    if (runtimeUrl) return runtimeUrl;
    // Fall back to build-time value (may be placeholder — will fail at
    // OIDC discovery, which is the intended fail-fast for misconfiguration).
    return KEYCLOAK_ISSUER_URL;
  }
  return KEYCLOAK_ISSUER_URL;
}

/**
 * Browser-facing Keycloak issuer URL, preserving the realm path but replacing
 * the static build-time origin with the current browser origin.
 *
 * On-prem deployments may be reached through multiple hostnames / IPs (LAN,
 * VPN, external) and non-standard HTTPS ports (e.g. :8443). The build-time
 * `KEYCLOAK_ISSUER_URL` is fixed at compile time, so a client-side logout URL
 * built from it can redirect the browser to a different domain than the one
 * the user is currently on. This helper keeps the logout redirect on the same
 * host + port as the running page.
 */
export function getBrowserKeycloakIssuerUrl(browserOrigin?: string): string {
  const origin =
    browserOrigin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  if (!origin || !KEYCLOAK_ISSUER_URL) return KEYCLOAK_ISSUER_URL;
  try {
    const issuer = new URL(KEYCLOAK_ISSUER_URL);
    const browser = new URL(origin);
    return `${browser.origin}${issuer.pathname}`;
  } catch {
    // Malformed URL or runtime placeholder — fall back to the static value so
    // the call site still has a URL to work with.
    return KEYCLOAK_ISSUER_URL;
  }
}

// External issuer URL: the canonical hostname Keycloak uses for the `iss`
// claim in ID tokens (derived from KC_HOSTNAME). MUST match the token's
// `iss` value for NextAuth / openid-client validation. Server-side only
// (no NEXT_PUBLIC_ prefix). Falls back to the public issuer URL when not
// explicitly set so existing single-domain on-prem deployments keep
// working without a config change.
const rawExternalIssuer = process.env.KEYCLOAK_ISS_EXTERNAL;
export const KEYCLOAK_EXTERNAL_ISSUER_URL: string = (() => {
  if (!isKeycloak) return rawExternalIssuer ?? '';
  if (rawExternalIssuer) return requireValidIssuerUrl(rawExternalIssuer);
  return KEYCLOAK_ISSUER_URL;
})();

// Internal issuer URL: the cluster-internal URL for server-to-server
// Keycloak calls (token refresh, userinfo, JWKS). Set via the on-prem
// ConfigMap. Falls back to the external issuer URL when not set.
const rawInternalIssuer = process.env.KEYCLOAK_INTERNAL_ISSUER_URL;
export const KEYCLOAK_INTERNAL_ISSUER_URL: string = (() => {
  if (!isKeycloak) return rawInternalIssuer ?? '';
  if (rawInternalIssuer) return requireValidIssuerUrl(rawInternalIssuer);
  return KEYCLOAK_EXTERNAL_ISSUER_URL;
})();

/**
 * Build a Keycloak RP-initiated-logout (`end_session_endpoint`) URL.
 * Shared by both web logout paths (`useAuthKeycloakWebPath` for the
 * user-initiated case, `keycloakLogout` for forced logout) so the param
 * shape can't drift between them.
 *
 * When an `idToken` is supplied it's sent as `id_token_hint`, which lets
 * Keycloak (v18+) identify the session and end it silently — without it
 * Keycloak shows a "Do you want to log out?" confirmation screen.
 * `client_id` is the fallback when the id token is unavailable.
 *
 * PII trade-off (compliance): the id token is a signed JWT carrying
 * identity claims (sub, email, name), and per the OIDC RP-Initiated
 * Logout spec (§3) it travels as a query parameter on a browser
 * navigation. It can therefore land in browser history and in
 * proxy/reverse-proxy access logs. On-prem deployments must ensure
 * Traefik / upstream LBs redact query strings from access logs (or
 * accept the trade-off in the on-prem security baseline). The token is
 * already short-lived and single-session, limiting exposure.
 */
export function buildKeycloakEndSessionUrl(params: {
  issuerUrl: string;
  idToken?: string | null;
  postLogoutRedirectUri: string;
}): string {
  const { issuerUrl, idToken, postLogoutRedirectUri } = params;
  const url = new URL(`${issuerUrl}/protocol/openid-connect/logout`);
  if (idToken) {
    url.searchParams.set('id_token_hint', idToken);
  } else {
    url.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);
  }
  url.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri);
  return url.toString();
}
